import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, fromJS } from 'immutable';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import FinalizeAndSignOff from 'src/screen/Protected/DeviceScreen/TestingReport/FinalizeAndSignOff';
import FieldReportModal from 'src/screen/Protected/DeviceScreen/Commissioning/FieldReportModal';
import NotesField from 'src/screen/Protected/DeviceScreen/Commissioning/NotesField';
import { useTranslation } from 'src/i18n';
import ProjectInfoTable from 'src/screen/Protected/DeviceScreen/Commissioning/ProjectInfoTable';
import HmsAuditInspection from './HmsAuditInspection';
import HmsAuditPanelInspection from './HmsAuditPanelInspection';
import HmsAuditCircuitInsulation from './HmsAuditCircuitInsulation';
import HmsAuditDeliverables from './HmsAuditDeliverables';

const REPORT_TYPE = 'audit-hms';
// Two task types so §2's blocks render as separately titled tables, as on the paper form.
const SCOPE_TYPE = 'audit-hms-scope';
const DOC_TYPE = 'audit-hms-doc';
const TASK_TYPES = [SCOPE_TYPE, DOC_TYPE];

const PROJECT_INFO_FIELDS = ['deviceTag', 'unitArea'];

// Device columns copied into the form when a device is linked, if still empty.
const PREFILL_FIELDS = [
  { path: ['projectInfo', 'deviceTag'], deviceKey: 'tag' },
  { path: ['projectInfo', 'unitArea'], deviceKey: 'unit' },
];

export default function HmsAuditReport (props) {
  const { t } = useTranslation();

  const tabs = useMemo(() => [
    { value: '1', label: t('hmsAudit.tabs.projectInfo') },
    { value: '2', label: t('hmsAudit.tabs.preAuditSummary') },
    { value: '3', label: t('hmsAudit.tabs.inspectionSummary') },
    { value: '4', label: t('hmsAudit.tabs.panelInspection') },
    { value: '5', label: t('hmsAudit.tabs.circuitAndInsulation') },
    { value: '6', label: t('hmsAudit.tabs.deliverablesAndSignOff') },
  ], [t]);

  const buildEmptyResults = useCallback((tasksByType, device) => {
    const emptyTasks = (type: string) => {
      const tasks = tasksByType.get(type);
      const tasksList = tasks?.toJS ? tasks.toJS() : [];
      // Raw redux rows use `task`; the getCheckSheetTasks selector renames it to `key`.
      return tasksList.map((task: any) => ({ task_id: task.task ?? task.key, status: null, comment: '' }));
    };
    const emptyCount = { count: '', verdict: '' };
    return fromJS({
      info: { type: REPORT_TYPE, deviceId: props.deviceId || null },
      [SCOPE_TYPE]: emptyTasks(SCOPE_TYPE),
      [DOC_TYPE]: emptyTasks(DOC_TYPE),
      extras: {
        projectInfo: {
          deviceTag: device?.get('tag') || '',
          unitArea: device?.get('unit') || '',
        },
        preAudit: { notes: '' },
        inspection: {
          dateFrom: '',
          dateTo: '',
          siteConditions: { averageTemperature: '', sunny: '', rain: '' },
          // The paper form lists these four instruments; seeded so the labels match it.
          equipmentUsed: [
            { equipment: t('hmsAudit.equipment.megger'), idNumber: '', calDueDate: '' },
            { equipment: t('hmsAudit.equipment.voltageMeter'), idNumber: '', calDueDate: '' },
            { equipment: t('hmsAudit.equipment.oscilloscope'), idNumber: '', calDueDate: '' },
            { equipment: t('hmsAudit.equipment.other'), idNumber: '', calDueDate: '' },
          ],
          inspectors: [{ name: '', titleCompany: '' }],
          clientReps: [{ name: '', titleCompany: '' }],
        },
        panelInspection: {
          powerPanelDeficiencies: '', controlPanelDeficiencies: '',
          branchCircuits: emptyCount, openBranchCircuits: emptyCount, shortBranchCircuits: emptyCount,
          controllerAlarms: emptyCount, controllerWarnings: emptyCount,
          recommendations: '',
        },
        circuitAudit: {
          visualDeficiencies: '',
          investigatedCircuits: emptyCount, openCircuits: emptyCount, shortCircuits: emptyCount,
          failedIrTests: emptyCount, failedOhmTests: emptyCount, operationalTestFailures: emptyCount,
          recommendations: '',
        },
        insulationAudit: {
          investigatedCircuits: emptyCount,
          code1: emptyCount, code2: emptyCount, code3: emptyCount, code4: emptyCount, other: emptyCount,
        },
        deliverables: { items: [{ deliverableType: '', countProduced: '' }], notes: '' },
      },
      signOff: {},
    }) as Map<string, any>;
  }, [props.deviceId, t]);

  const renderTab = useCallback((tabValue, ctx) => {
    const { allResults, extras, change, readOnly, statusError, linkedDeviceTag, handleUnlinkDevice, handleDeviceSelect } = ctx;
    switch (tabValue) {
      case '1':
        return (
          <ProjectInfoTable
            fields={PROJECT_INFO_FIELDS}
            i18nPrefix='hmsAudit'
            tagField='deviceTag'
            readOnly={readOnly}
            data={extras}
            onChange={change}
            linkedDeviceTag={linkedDeviceTag}
            onUnlink={handleUnlinkDevice}
            onDeviceSelect={handleDeviceSelect}
          />
        );
      case '2':
        return (
          <>
            <CheckSheet
              readOnly={readOnly}
              onChange={change}
              id={SCOPE_TYPE}
              title={t('hmsAudit.auditScope')}
              data={allResults?.get(SCOPE_TYPE)}
              checkSheetType={SCOPE_TYPE}
            />
            <CheckSheet
              readOnly={readOnly}
              onChange={change}
              id={DOC_TYPE}
              title={t('hmsAudit.documentationProvided')}
              data={allResults?.get(DOC_TYPE)}
              checkSheetType={DOC_TYPE}
            />
            <NotesField
              title={t('hmsAudit.preAuditNotes')}
              value={extras?.getIn(['preAudit', 'notes'])}
              path={['extras', 'preAudit', 'notes']}
              onChange={change}
              readOnly={readOnly}
            />
          </>
        );
      case '3':
        return <HmsAuditInspection readOnly={readOnly} data={extras} onChange={change} />;
      case '4':
        return <HmsAuditPanelInspection readOnly={readOnly} data={extras} onChange={change} />;
      case '5':
        return <HmsAuditCircuitInsulation readOnly={readOnly} data={extras} onChange={change} />;
      case '6':
        return (
          <>
            <HmsAuditDeliverables readOnly={readOnly} data={extras} onChange={change} />
            {/* The sign-off notes box carries the deliverable notes, so the deliverables
                section above doesn't repeat one. */}
            <FinalizeAndSignOff
              readOnly={readOnly}
              data={allResults?.get('signOff')}
              onChange={change}
              notesLabel={t('hmsAudit.deliverables.notes')}
              notesValue={extras?.getIn(['deliverables', 'notes'])}
              notesPath={['extras', 'deliverables', 'notes']}
            />
          </>
        );
      default:
        return null;
    }
  }, [t]);

  return (
    <FieldReportModal
      reportType={REPORT_TYPE}
      taskTypes={TASK_TYPES}
      tabs={tabs}
      title={{ label: t('hmsAudit.modalTitle') }}
      prefillFields={PREFILL_FIELDS}
      requiresStatus={false}
      buildEmptyResults={buildEmptyResults}
      renderTab={renderTab}
      deviceId={props.deviceId}
      reportId={props.reportId}
      open={props.open}
      handleClose={props.handleClose}
    />
  );
}

HmsAuditReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
